import { CartObserverProvider } from "../../cart";
import { SetupAttributeType } from "../types";
import { EcommercePrimaryAttributes } from "./types";

export function prepare() {
  if (!window.helium) {
    window.helium = {} as any;
  }

  window.helium.attributes = {
    ...(window?.helium?.attributes || {}),
    ecommerce: {
      ...(window?.helium?.attributes?.ecommerce || {}),
      primary: {
        ...(window?.helium?.attributes?.ecommerce?.primary || {}),
      },
      secondary: {
        ...(window?.helium?.attributes?.ecommerce?.secondary || {}),
      },
    },
  };
}

export const ecommercePrimaryAttributes: SetupAttributeType<EcommercePrimaryAttributes> =
  [
    {
      id: "cartItemsList",
      setup: () => {
        fetch(window.location.origin + "/cart.js")
          .then((response) => response.json())
          .then((cart) => {
            const itemCount = cart.item_count || 0;
            const cartItemsList =
              itemCount > 5 ? "High" : itemCount > 2 ? "Medium" : "Low";

            window.helium.attributes.ecommerce.primary.cartItemsList =
              cartItemsList;
          });
      },
    },

    {
      id: "cartAbandonmentRate",
      setup: () => {
        let abandonmentEvents =
          JSON.parse(localStorage.getItem("abandonmentEvents")) || 0;
        let totalCartInteractions =
          JSON.parse(localStorage.getItem("totalCartInteractions")) || 1;

        const isCartEmpty = () => {
          const cartInfo = window.helium.attributes.ecommerce.primary
            .cartInfo || {
            itemCount: 0,
          };
          return cartInfo.itemCount === 0;
        };

        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "hidden" && !isCartEmpty()) {
            abandonmentEvents++;
            localStorage.setItem(
              "abandonmentEvents",
              JSON.stringify(abandonmentEvents),
            );
          } else if (document.visibilityState === "visible" && !isCartEmpty()) {
            abandonmentEvents = Math.max(0, abandonmentEvents - 1);
            localStorage.setItem(
              "abandonmentEvents",
              JSON.stringify(abandonmentEvents),
            );
          }
        });

        const originalLogCartOperation = CartObserverProvider.logCartOperation;
        CartObserverProvider.logCartOperation = (action, args) => {
          originalLogCartOperation.call(CartObserverProvider, action, args);
          totalCartInteractions++;
          localStorage.setItem(
            "totalCartInteractions",
            JSON.stringify(totalCartInteractions),
          );
        };

        const cartAbandonmentRate =
          (abandonmentEvents / totalCartInteractions) * 100;

        const abandonmentCategory =
          cartAbandonmentRate > 60
            ? "Very High"
            : cartAbandonmentRate > 40
              ? "High"
              : cartAbandonmentRate > 20
                ? "Medium"
                : cartAbandonmentRate > 10
                  ? "Low"
                  : "Very Low";

        window.helium.attributes.ecommerce.primary.cartAbandonmentRate =
          abandonmentCategory;

        return window.helium.attributes.ecommerce.primary.cartAbandonmentRate;
      },
    },
    {
      id: "beginCheckout",
      setup: () => {
        Array.from(document.querySelectorAll("button[name='checkout']"))
          .concat(
            Array.from(document.querySelectorAll("button[id='button-gokwik']")),
            Array.from(document.querySelectorAll("button")).filter(
              (button) =>
                button.textContent && /buy|checkout/i.test(button.textContent),
            ),
          )
          .forEach((button) => {
            button.addEventListener("click", () => {
              window.helium.attributes.ecommerce.primary.beginCheckout =
                "Started";
            });
          });

        return (
          window.helium.attributes.ecommerce.primary.beginCheckout ||
          "Not Started"
        );
      },
    },
    {
      id: "removeFromCart",
      setup: () => {
        const removeCount =
          CartObserverProvider.getCartOperationCount("remove");
        const removeCategory =
          removeCount > 5 ? "High" : removeCount > 2 ? "Medium" : "Low";

        window.helium.attributes.ecommerce.primary.removeFromCart =
          removeCategory;

        return window.helium.attributes.ecommerce.primary.removeFromCart;
      },
    },
    {
      id: "maxPreferredPriceRange",
      setup: () => {
        const previousPreferredPriceRange = JSON.parse(
          localStorage.getItem("preferredPriceRange"),
        ) || { minPrice: 0, maxPrice: 0 };

        const productPrices =
          window.helium.attributes.ecommerce.primary.cartInfo.itemDetails.map(
            (item) => item.price,
          );

        const minPrice = Math.min(
          ...productPrices,
          previousPreferredPriceRange.minPrice,
        );
        const maxPrice = Math.max(
          ...productPrices,
          previousPreferredPriceRange.maxPrice,
        );

        const maxPriceCategory =
          maxPrice > 1000 ? "High" : maxPrice > 500 ? "Medium" : "Low";

        window.helium.attributes.ecommerce.primary.maxPreferredPriceRange =
          maxPrice || previousPreferredPriceRange.maxPrice;

        window.helium.attributes.ecommerce.primary.minPreferredPriceRange =
          maxPriceCategory;

        // window.helium.attributes.ecommerce.primary.preferredPriceRange = {
        //   minPrice: minPrice || previousPreferredPriceRange.minPrice,
        //   maxPrice: maxPrice || previousPreferredPriceRange.maxPrice,
        // };

        localStorage.setItem(
          "preferredPriceRange",
          JSON.stringify({
            minPrice: minPrice || previousPreferredPriceRange.minPrice,
            maxPrice: maxPrice || previousPreferredPriceRange.maxPrice,
          }),
        );

        return window.helium.attributes.ecommerce.primary.preferredPriceRange;
      },
    },
    {
      id: "orderAbandoned",
      setup: () => {
        let orderAbandoned = false;
        const originalLogCartOperation = CartObserverProvider.logCartOperation;
        CartObserverProvider.logCartOperation = (action, args) => {
          originalLogCartOperation.call(CartObserverProvider, action, args);

          if (action === "remove") {
            orderAbandoned = true;
            localStorage.setItem(
              "orderAbandoned",
              JSON.stringify(orderAbandoned),
            );
          }
        };

        window.helium.attributes.ecommerce.primary.orderAbandoned =
          orderAbandoned || JSON.parse(localStorage.getItem("orderAbandoned"))
            ? "Yes"
            : "No";

        return window.helium.attributes.ecommerce.primary.orderAbandoned;
      },
    },
    {
      id: "averageOrderValue",
      setup: () => {
        const completedOrders =
          JSON.parse(localStorage.getItem("completedOrders")) || [];
        const totalOrderValue = completedOrders.reduce(
          (total, order) => total + order.totalValue,
          0,
        );
        const averageOrderValue =
          completedOrders.length > 0
            ? totalOrderValue / completedOrders.length
            : 0;

        const maxOrderValue = 5_000;
        const normalizedAverageOrderValue = Math.min(
          100,
          (averageOrderValue / maxOrderValue) * 100,
        );

        const orderValueCategory =
          normalizedAverageOrderValue > 60
            ? "High"
            : normalizedAverageOrderValue > 30
              ? "Medium"
              : "Low";

        window.helium.attributes.ecommerce.primary.averageOrderValue =
          orderValueCategory;

        localStorage.setItem(
          "averageOrderValue",
          JSON.stringify(orderValueCategory),
        );

        return window.helium.attributes.ecommerce.primary.averageOrderValue;
      },
    },
  ];
